import React, { useState, useEffect, useRef, useCallback } from 'react';
import ChatBot from './ChatBot';
import './FloatingWidget.css';

const FloatingChatWidget = ({ 
  position = 'bottom-right',
  theme = 'healthcare',
  showWelcomeMessage = true,
  autoOpen = false,
  notificationSound = true
}) => {
  const [isOpen, setIsOpen] = useState(autoOpen);
  const [hasNewMessage, setHasNewMessage] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [sessionData, setSessionData] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [unreadCount, setUnreadCount] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [connectionQuality, setConnectionQuality] = useState('good'); // 'excellent', 'good', 'fair', 'poor'
const [lastActivity, setLastActivity] = useState(Date.now());
  
  // Animation and interaction states
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  
  // Refs for managing timers and audio
  const notificationTimeoutRef = useRef(null);
  const tooltipTimeoutRef = useRef(null);
  const audioRef = useRef(null);
  const widgetRef = useRef(null);
  
  // Notification sound
  useEffect(() => {
    if (notificationSound && typeof Audio !== 'undefined') {
      audioRef.current = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmEcBz2Y3/LCdSMFMIvQ8daHNAYedL/s4psODlOo4u+zXCEEOITO8+CI');
    }
  }, [notificationSound]);

  // Handle new messages from ChatBot
  const handleNewMessage = useCallback(() => {
    if (!isOpen) {
      setHasNewMessage(true);
      setUnreadCount(prev => prev + 1);
      
      // Play notification sound
      if (audioRef.current && notificationSound) {
        audioRef.current.play().catch(console.warn);
      }
      
      // Auto-clear notification after 5 seconds
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
      }
      notificationTimeoutRef.current = setTimeout(() => {
        setHasNewMessage(false);
      }, 5000);
    }
  }, [isOpen, notificationSound]);

  // Handle session changes from ChatBot
  const handleSessionChange = useCallback((session) => {
    setSessionData(session);
  }, []);

  // Toggle chat window
  const toggleChat = useCallback(() => {
    setIsOpen(prev => {
      const newIsOpen = !prev;
      if (newIsOpen) {
        setHasNewMessage(false);
        setUnreadCount(0);
        setIsMinimized(false);
        if (notificationTimeoutRef.current) {
          clearTimeout(notificationTimeoutRef.current);
        }
      }
      return newIsOpen;
    });
  }, []);



  // Minimize/maximize chat
  const toggleMinimize = useCallback(() => {
    setIsMinimized(prev => !prev);
  }, []);

  // Close chat completely
  const closeChat = useCallback(() => {
    setIsOpen(false);
    setIsMinimized(false);
  }, []);

  // Handle tooltip display
  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
    if (!isOpen && showWelcomeMessage) {
      tooltipTimeoutRef.current = setTimeout(() => {
        setShowTooltip(true);
      }, 1000);
    }
  }, [isOpen, showWelcomeMessage]);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    setShowTooltip(false);
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }
  }, []);


  // Handle connection status changes from ChatBot
const handleConnectionStatusChange = useCallback((status) => {
  setConnectionStatus(status);
}, []);


// Enhanced status display
const getStatusDisplay = () => {
  switch (connectionStatus) {
    case 'connected':
      return 'Online';
    case 'sending':
      return 'Sending...';
    case 'connecting':
      return 'Connecting...';
    case 'error':
      return 'Connection Error';
    default:
      return 'Offline';
  }
};

const getStatusColor = () => {
  switch (connectionStatus) {
    case 'connected':
      return '#22c55e'; // Green
    case 'sending':
      return '#f59e0b'; // Yellow
    case 'connecting':
      return '#3b82f6'; // Blue
    case 'error':
      return '#ef4444'; // Red
    default:
      return '#6b7280'; // Gray
  }
};

  // Handle button press animation
  const handleMouseDown = useCallback(() => {
    setIsPressed(true);
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsPressed(false);
  }, []);

  // Keyboard accessibility
  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleChat();
    } else if (event.key === 'Escape' && isOpen) {
      closeChat();
    }
  }, [toggleChat, closeChat, isOpen]);

  // Auto-hide widget on scroll (optional)
  useEffect(() => {
    let scrollTimer = null;
    const handleScroll = () => {
      setIsVisible(false);
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        setIsVisible(true);
      }, 150);
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => {
        window.removeEventListener('scroll', handleScroll);
        clearTimeout(scrollTimer);
      };
    }
  }, []);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
      }
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);

  // Add this useEffect
useEffect(() => {
  const monitorConnection = () => {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (connection) {
      const updateQuality = () => {
        if (connection.effectiveType === '4g') {
          setConnectionQuality('excellent');
        } else if (connection.effectiveType === '3g') {
          setConnectionQuality('good');
        } else if (connection.effectiveType === '2g') {
          setConnectionQuality('fair');
        } else {
          setConnectionQuality('poor');
        }
      };
      
      connection.addEventListener('change', updateQuality);
      updateQuality();
      
      return () => connection.removeEventListener('change', updateQuality);
    }
  };

  return monitorConnection();
}, []);

  // Dynamic classes for the toggle button
  const toggleButtonClasses = [
    'chat-toggle-btn',
    position,
    theme,
    hasNewMessage && 'has-notification',
    isHovered && 'hovered',
    isPressed && 'pressed',
    !isVisible && 'hidden'
  ].filter(Boolean).join(' ');

  // Dynamic classes for the chat widget
  const widgetClasses = [
    'floating-chat-widget',
    position,
    theme,
    isMinimized && 'minimized',
    connectionStatus
  ].filter(Boolean).join(' ');

  return (
    <>
      {/* Chat Widget */}
      {isOpen && (
        <div 
          ref={widgetRef}
          className={widgetClasses}
          role="dialog"
          aria-label="KaireAI Chat Assistant"
          aria-modal="true"
        >
          {/* Widget Header */}
          <div className="widget-header">
            <div className="widget-title">
              <div className="bot-avatar">
                <img 
                  src="https://res.cloudinary.com/daccflyf2/image/upload/v1757683579/kaire-logo_c0llam.png" 
                  alt="KaireCare" 
                />
              <div 
  className="avatar-status-indicator"
  style={{ backgroundColor: getStatusColor() }}
></div>
              </div>
              <div className="title-info">
                <h4>KaireAI Health Companion</h4>
                <div className="status-info">
                  <span className="online-status">
                   <span 
                    className="status-dot"
                    style={{ backgroundColor: getStatusColor() }}
                    ></span>
                    {getStatusDisplay()}
                  </span>
                  {sessionData?.messageCount > 0 && (
                    <span className="message-count">
                      {sessionData.messageCount} messages
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            <div className="header-actions">
              <button 
                className="minimize-btn"
                onClick={toggleMinimize}
                title={isMinimized ? "Expand chat" : "Minimize chat"}
                aria-label={isMinimized ? "Expand chat" : "Minimize chat"}
              >
                {isMinimized ? '▲' : '▼'}
              </button>
              <button 
                className="close-btn"
                onClick={closeChat}
                title="Close chat"
                aria-label="Close chat"
              >
                ×
              </button>
            </div>
          </div>

          {/* Chat Content */}
          {!isMinimized && (
            <div className="widget-content">
              <ChatBot 
                onNewMessage={handleNewMessage}
                onSessionChange={handleSessionChange}
                onConnectionStatusChange={handleConnectionStatusChange}
/>
            </div>
          )}

          {/* Minimized State */}
          {isMinimized && (
            <div className="minimized-content">
              <div className="minimized-info">
                <span>Chat minimized</span>
                <button 
                  className="restore-btn"
                  onClick={toggleMinimize}
                  title="Restore chat"
                >
                  Click to restore
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Toggle Button with Tooltip */}
      <div className="chat-toggle-container">
        {/* Welcome Tooltip */}
        {showTooltip && !isOpen && (
          <div className="welcome-tooltip">
            <div className="tooltip-content">
              <div className="tooltip-header">
                <img 
                  src="https://res.cloudinary.com/daccflyf2/image/upload/v1757413277/kaireai-nurse_pjjnpu.jpg" 
                  alt="KaireAI" 
                  className="tooltip-avatar"
                />
                <div>
                  <h4>Hi! I'm your KaireAI Assistant</h4>
                  <p>I'm here to help with your healthcare needs</p>
                </div>
              </div>
              <div className="tooltip-features">
                <div className="tooltip-feature">
                  <span className="feature-icon">🏥</span>
                  <span>KaireAI Features</span>
                </div>
                <div className="tooltip-feature">
                  <span className="feature-icon">📅</span>
                  <span>KaireAI for Caregivers</span>
                </div>
                <div className="tooltip-feature">
                  <span className="feature-icon">🚨</span>
                  <span>GoodBye</span>
                </div>
              </div>
              <button className="tooltip-cta" onClick={toggleChat}>
                Start Chat
              </button>
            </div>
            <div className="tooltip-arrow"></div>
          </div>
        )}

        {/* Main Toggle Button */}
        <button 
          className={toggleButtonClasses}
          onClick={toggleChat}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onKeyDown={handleKeyDown}
          title="Chat with KaireAI Assistant"
          aria-label={`${isOpen ? 'Close' : 'Open'} KaireAI chat assistant`}
          aria-expanded={isOpen}
          aria-haspopup="dialog"
        >
          {/* Button Content */}
          <div className="toggle-btn-content">
            {isOpen ? (
              <span className="close-icon">×</span>
            ) : (
              <>
                <img 
                  src="https://res.cloudinary.com/daccflyf2/image/upload/v1757413277/kaireai-nurse_pjjnpu.jpg" 
                  alt="KaireAI Assistant" 
                  className="toggle-avatar"
                />
                <div className="pulse-ring"></div>
                <div className="pulse-ring pulse-ring-delayed"></div>
              </>
            )}
          </div>

          {/* Notification Badge */}
          {hasNewMessage && unreadCount > 0 && (
            <div className="notification-badge" aria-label={`${unreadCount} unread messages`}>
              {unreadCount > 99 ? '99+' : unreadCount}
            </div>
          )}

          {/* Status Indicator */}
        <div 
  className={`connection-indicator ${connectionStatus}`}
  style={{ backgroundColor: getStatusColor() }}
></div>
        </button>

        {/* Quick Actions (when closed) */}
        {!isOpen && isHovered && (
          <div className="quick-actions">
            <button 
              className="quick-action-btn emergency"
              onClick={() => {
                toggleChat();
                // You can add emergency-specific logic here
              }}
              title="GoodBye"
              aria-label="bye"
            >
              🚨
            </button>
            <button 
              className="quick-action-btn appointment"
              onClick={() => {
                toggleChat();
                // You can add appointment-specific logic here
              }}
              title="Book Appointment"
              aria-label="Book an appointment"
            >
              📅
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default FloatingChatWidget;